import { Dispatch, SetStateAction, FocusEvent } from 'react'
import { Listbox, Transition } from '@headlessui/react'

import DropdownIcon from './DropdownIcon'
import SelectedIcon from './SelectedIcon'

// list [{ name, key }]

function createLabel(value, multiple) {
  if (multiple && value.length < 1) {
    return null
  }

  if (!multiple) {
    return value?.name ?? null
  }

  const names = value.map((item) => item.name)
  switch (value.length) {
    case 1:
      return `${names[0]}`
    case 2:
      return `${names[0]}, ${names[1]}`
    default:
      return `${names[0]}, ${names[1]}, otros...`
  }
}

export interface ListItem {
  key: string
  name: string
  [x: string]: any
}

interface CustomListboxProps {
  list: ListItem[]
  selectedValue: ListItem
  setSelectedValue: (a: ListItem) => void
  label?: string
  id?: string
  disabled?: boolean
  icon?: JSX.Element
  onBlur?: (e: FocusEvent) => void
  multiple?: boolean
  isError?: boolean
  errorMessage?: string
  className?: string
  listClassName?: string
  [x: string]: any
}

const CustomListbox = ({
  list,
  selectedValue,
  setSelectedValue,
  id,
  disabled = false,
  label = null,
  icon = null,
  onBlur = (e) => null,
  multiple = false,
  isError = false,
  errorMessage = null,
  className = '',
  listClassName = '',
  ...otherProps
}: CustomListboxProps) => (
  <>
    <Listbox
      as='div'
      className='relative'
      multiple={multiple}
      value={selectedValue}
      onChange={setSelectedValue}
      onBlur={(e) => onBlur(e)}
      disabled={disabled}
      {...otherProps}
    >
      {({ open }) => (
        <>
          <Listbox.Button
            onBlur={(e) => !open && onBlur(e)}
            id={id}
            className={`w-full flex-row flex justify-between gap-2 items-center text-left h-14 px-2 border focus:outline-purple-900 focus:ring-gray-300 focus:ring-4 focus:ring-offset-0 focus:border-purple-900 active:outline-purple-900 active:ring-gray-300 active:ring-4 active:ring-offset-0 active:border-purple-900 text-sm text-black rounded placeholder-purple-900 small-text-placeholder disabled:text-gray-500 font-medium ${
              disabled
                ? 'bg-[#f5f5f4] focus:outline-none border border-gray-500 focus:ring-0 focus:border-none  active:border-gray-500 active:outline-gray-500 active:ring-0'
                : 'bg-white'
            } ${
              isError ? 'border-2 border-red-900' : 'border-gray-500'
            } ${className}`}
            {...otherProps}
          >
            <div className='flex align-center items-center h-full'>
              <div className={icon && 'pl-2'}>{icon}</div>
              <div
                className={`${
                  icon && 'pl-6'
                } flex flex-grow-0 flex-col truncate`}
              >
                <p
                  className={`text-xs text-black ${
                    !!selectedValue && '!text-2xs'
                  }`}
                >
                  {label}
                </p>
                <p className='truncate max-w-[250px]'>
                  {createLabel(selectedValue, multiple)}
                </p>
              </div>
            </div>
            <DropdownIcon className='w-5 flex-shrink-0 h-5 self-center' />
          </Listbox.Button>
          <Transition
            show={open}
            enter='transition duration-100 ease-out'
            enterFrom='transform scale-95 opacity-0'
            enterTo='transform scale-100 opacity-100'
            leave='transition duration-75 ease-out'
            leaveFrom='transform scale-100 opacity-100'
            leaveTo='transform scale-95 opacity-0'
            className='absolute z-10 mt-1 w-full'
          >
            <Listbox.Options
              static
              className={`max-h-40 z-50 w-full absolute overflow-auto text-sm rounded-md leading-5 py-2 bg-white focus:outline-none mt-1 divide-gray-500 border border-gray-500 divide-y-1 ${listClassName}`}
            >
              {list.map((item, idx) => (
                <Listbox.Option
                  as='div'
                  key={item.key || item.uuid || item.cbu || item.id || idx}
                  disabled={disabled}
                  value={item}
                >
                  {({ active, selected }) => (
                    <div
                      className={`truncate text-black py-2 px-2 cursor-pointer flex justify-between ${
                        active && 'bg-gray-300 text-black'
                      } ${
                        selected && 'text-green-800 font-medium select-none'
                      }`}
                    >
                      <p className='truncate'>{item.name}</p>
                      {selected && (
                        <SelectedIcon className='min-w-max pl-1 self-center' />
                      )}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </>
      )}
    </Listbox>
    {isError && errorMessage && (
      <div className='h-4'>
        <p className='text-red-900 text-left pt-1 font-bold text-2xs pl-1'>
          {errorMessage}
        </p>
      </div>
    )}
  </>
)

export default CustomListbox
